/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable, Injector} from '@angular/core';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';

import {OBJECT_TYPE, ObjectTypeFinder} from '../../app/common/constants/object-type';
import {UserProfileResource} from '../api/resources/resource/user-profile.resource';
import {LocalNetworkProfile} from './local-network-profile';
import {LocalUserProfile} from './local-user-profile';

/**
 * Root injector, set at bootstrap so Resource.get can lazy-resolve this saver
 * (and StateService) without threading them through every resource factory —
 * same idea as AngularJS adnResource's $injector.get("UserProfileSaver").
 */
let rootInjector: Injector | null = null;

export function setUserProfileSaverInjector(injector: Injector | null): void {
  rootInjector = injector;
}

/**
 * AngularJS adnResource.get called objRegister after each successful GET.
 * Restore that here: only the page's main object (state type matches url type),
 * skipping Workspace.
 */
export function registerLastViewedFromResourceGet(obj: any): void {
  if (!obj || !rootInjector) {
    return;
  }
  // Deferred so nested/side GETs settle and $state.current reflects the page.
  setTimeout(() => {
    if (!rootInjector) {
      return;
    }
    const stateName = rootInjector.get(StateService, null)?.current?.name;
    const object1 = ObjectTypeFinder.findObjectFromUrl(stateName);
    const object2 = ObjectTypeFinder.findObjectFromUrl(obj.url);
    if (object1 && object1 === object2 && object1 !== OBJECT_TYPE.Workspace) {
      rootInjector.get(UserProfileSaverService).saveObj(obj, 'adnLastViewed', object2);
    }
  }, 500);
}

@Injectable({
  providedIn: 'root'
})
export class UserProfileSaverService {
  constructor(
    // Factory product — duck-typed resource API (save/get/...).
    @Inject(UserProfileResource) private readonly UserProfile: any,
    private readonly localUserProfile: LocalUserProfile,
    private readonly LocalNetworkProfile: LocalNetworkProfile
  ) {
  }

  saveObj(obj: any, profileKeyPrefix: string, objType?: any): void {
    if (!obj.url) {
      return;
    }

    const oType = objType || ObjectTypeFinder.findObjectFromUrl(obj.url);
    if (!(obj.id && obj.name && oType)) {
      return;
    }

    const networkId = this.LocalNetworkProfile.getNetworkId();
    const app = this.localUserProfile.getCurrentApp();
    const profileKey = profileKeyPrefix + networkId + (app === 'ADVERTISING' ? '' : app);
    let localUser = this.localUserProfile.get();
    let profile = localUser.profile;

    profile[profileKey] = _.isArray(profile[profileKey]) ? profile[profileKey] : [];
    profile[profileKey].unshift({
      id: obj.id,
      objectType: oType.type,
      name: obj.name
    });
    profile[profileKey] = _.uniqBy(profile[profileKey], 'id');
    profile[profileKey] = _.slice(profile[profileKey], 0, 10);

    this.localUserProfile.publish(profileKey, profile[profileKey]);

    this.UserProfile.save({
      id: localUser.userId,
      profile: _.pick(profile, [profileKey])
    }, (apiUser: any) => {
      localUser = this.localUserProfile.safeSaveProfile(profileKey, apiUser.profile[profileKey]);
      profile = localUser.profile;
    });
  }
}
